/**
 * parentRedirect — pure helper for the three redirects
 * ParentShellInner must perform when the parent's auth/consent
 * state doesn't match the requested screen.
 *
 * Why a pure helper? ParentShellInner used to call `navigateTo`
 * directly during render (three sites), which set state on the
 * sibling AppShell component while a different component was
 * rendering. React 18+ surfaces that as a dev-mode warning:
 *
 *   "Cannot update a component (AppShell) while rendering a
 *   different component (ParentShellInner)"
 *
 * The fix is to compute the redirect target from the current
 * snapshot of auth/consent state inside the render, then fire
 * `navigateTo` inside a useEffect keyed on those same inputs.
 * Extracting the decision into a pure function lets us pin the
 * redirect contract with a unit test (this file's mirror in
 * `web/test/parent.test.ts`) without needing a DOM renderer to
 * observe the effect.
 *
 * The hand-rolled routing stays — this ticket is about React's
 * render rules, not refactoring to react-router.
 */
export type ParentScreenKind =
  | 'parent-sign-in'
  | 'parent-consent'
  | 'parent-dashboard'
  | 'parent-goals'
  | 'parent-thread';

/**
 * `consentOk === null` means the probe hasn't run yet. ParentShellInner
 * renders the probe component in that state, so the redirect must
 * NOT fire — otherwise the user would ping-pong between consent and
 * the probe on every probe-resolved cycle.
 */
export type ConsentState = boolean | null;

export interface ParentRedirectInput {
  hasSession: boolean;
  consentOk: ConsentState;
  screenKind: ParentScreenKind;
}

export type ParentRedirectTarget =
  | '/parent/sign-in'
  | '/parent/consent'
  | '/parent/dashboard';

/**
 * Returns the path ParentShellInner should redirect to, or null
 * if the current screen is the correct destination for the given
 * auth/consent snapshot.
 *
 * Rules (preserve the existing render-time behavior exactly):
 *   1. No session + not on sign-in → /parent/sign-in
 *   2. Session + consentOk === false + not on consent
 *      → /parent/consent
 *   3. Session + consentOk === true + on sign-in
 *      → /parent/dashboard
 *   4. Otherwise (incl. consentOk === null) → null
 */
export function pickParentRedirect(
  input: ParentRedirectInput,
): ParentRedirectTarget | null {
  const { hasSession, consentOk, screenKind } = input;

  if (!hasSession) {
    if (screenKind === 'parent-sign-in') return null;
    return '/parent/sign-in';
  }

  if (consentOk === null) {
    // Probe still resolving — don't redirect; the probe owns the
    // pre-render gate.
    return null;
  }

  if (!consentOk) {
    if (screenKind === 'parent-consent') return null;
    return '/parent/consent';
  }

  // consentOk === true
  if (screenKind === 'parent-sign-in') return '/parent/dashboard';
  return null;
}